import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UnitComparisonRadarChart from './UnitComparisonRadarChart';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import { useReactiveVar } from '@apollo/client';
import { selectedPracticeArea } from '../appraisalComparisonCache';
import RadarChartButtons from './RadarChartButtons';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';

const maxPageSize = 15;

/**
 * @typedef {object} UnitComparisonRadarProps
 * @property {Object} [selectedComparison]
 * @property {Object} [orgReportData]
 * @property {Array<Object>} [unitAppraisalData]
 * @property {Object} [industryViewReportData]
 * @property {number} [selectedProgramYearValue]
 * @property {boolean} loading
 */

/**
 * @param {UnitComparisonRadarProps} props
 */
const UnitComparisonRadar = props => {
  const {
    selectedProgramYearValue,
    loading,
    selectedComparison = {
      key: FILTER_TYPES_KEYS.Industry
    },
    unitAppraisalData = [],
    orgReportData = {},
    industryViewReportData = {}
  } = props;

  const selectedPracticeAreaId = useReactiveVar(selectedPracticeArea);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const totalData = useMemo(() => {
    const industryPracticeAreas =
      industryViewReportData?.appraisalComparisonView?.PracticeAreas || [];
    const organizationPracticeAreas =
      orgReportData?.organizationAppraisalReport?.PracticeAreas || [];

    const industryGraphData = industryPracticeAreas?.map(element => ({
      ids: element.Ids,
      subject: element.Name,
      acronym: element.Acronym,
      I: element.AggregatePercentageExact
    }));

    const orgGraphData = organizationPracticeAreas?.map(element => ({
      ids: element.Ids,
      subject: element.Name,
      acronym: element.Acronym,
      O: element.AggregatePercentageExact
    }));

    const unitGraphData = unitAppraisalData?.map(element => ({
      id: element.PracticeArea.Id,
      ids: [element.PracticeArea.Id],
      subject: element.PracticeArea.Name,
      acronym: element.PracticeArea.Acronym,
      U: element.AggregatePercentage,
      fullMark: 100
    }));

    const totalData = unitGraphData.map(item => {
      const matchingIndustryData = industryGraphData.find(industryItem =>
        industryItem.ids.includes(item.id)
      );
      const matchingOrgData = orgGraphData.find(orgItem => orgItem.ids.includes(item.id));

      return {
        ...item,
        ...matchingIndustryData,
        ...matchingOrgData
      };
    });
    return totalData;
  }, [industryViewReportData, orgReportData, unitAppraisalData]);

  const pages = initPages(totalData.length);
  const startIndex = pages.slice(0, currentPageIndex).reduce((a, b) => a + b, 0);
  const endIndex = startIndex + pages[currentPageIndex];
  const _isReportDataMissing = isReportDataMissing(
    industryViewReportData?.appraisalComparisonView?.PracticeAreas,
    orgReportData?.organizationAppraisalReport?.PracticeAreas,
    selectedProgramYearValue,
    selectedComparison
  );

  const graphData = _isReportDataMissing
    ? []
    : pages.length > 0
      ? totalData.slice(startIndex, endIndex)
      : totalData;

  const isPrevDisabled = currentPageIndex <= 0;
  const isNextDisabled = currentPageIndex >= pages.length - 1;

  const goToPrevGraphPage = () => {
    setCurrentPageIndex(currentPageIndex - 1);
  };

  const goToNextGraphPage = () => {
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const goToPageContainsIndex = useCallback(
    practiceAreaIndex => {
      const pageSizes = initPages(totalData.length);
      let index = 0;
      let currentLen = 0;
      for (let i = 0; i < pageSizes.length; i++) {
        if (practiceAreaIndex >= currentLen && practiceAreaIndex < currentLen + pageSizes[i]) {
          index = i;
          break;
        }
        currentLen += pageSizes[i];
      }
      setCurrentPageIndex(index);
    },
    [totalData.length]
  );

  useEffect(() => {
    if (!selectedPracticeAreaId) {
      return;
    }

    if (!totalData?.length) {
      return;
    }

    const practiceAreaIndex = totalData.findIndex(e => e.ids.includes(selectedPracticeAreaId));
    if (practiceAreaIndex !== -1) {
      goToPageContainsIndex(practiceAreaIndex);
    }
  }, [selectedPracticeAreaId, totalData, goToPageContainsIndex]);

  return (
    <>
      <h4>{selectedComparison.value}</h4>
      <p>
        {selectedComparison.key === FILTER_TYPES_KEYS.OrgVsInd
          ? 'Displays the average organization % to the average industry % for each PA in scope for the unit.'
          : selectedComparison.key === FILTER_TYPES_KEYS.Industry
            ? 'Displays unit % for each PA as it compares to the average industry %.'
            : 'Displays unit % for each PA as it compares to the average organization %.'}
      </p>
      <SkeletonWrapper
        show={loading}
        skeletonClassName='section-data-skeleton'
        renderComponent={() => (
          <>
            <UnitComparisonRadarChart data={graphData} selectedDataKey={selectedComparison.key} />
            {pages && pages.length > 0 && (
              <RadarChartButtons
                leftRightId='unit-comparison-chart'
                isLeftDisabled={isPrevDisabled}
                isRightDisabled={isNextDisabled}
                onClickLeft={goToPrevGraphPage}
                onClickRight={goToNextGraphPage}
              />
            )}
          </>
        )}
      />
    </>
  );
};

const isReportDataMissing = (
  industryPracticeAreas,
  organizationPracticeAreas,
  selectedProgramYearValue,
  selectedComparison
) => {
  if (!industryPracticeAreas || !organizationPracticeAreas) {
    return true;
  }

  return (
    (industryPracticeAreas.length > 0 &&
      selectedProgramYearValue != industryPracticeAreas[0].Year) ||
    (selectedComparison.key == FILTER_TYPES_KEYS.Industry && !industryPracticeAreas.length) ||
    (selectedComparison.key == FILTER_TYPES_KEYS.Organization &&
      !organizationPracticeAreas.length) ||
    (selectedComparison.key == FILTER_TYPES_KEYS.OrgVsInd &&
      (!industryPracticeAreas.length || !organizationPracticeAreas.length))
  );
};

const initPages = len => {
  let pages = [];
  if (len <= maxPageSize) return pages;
  let index = 0;

  while (len >= maxPageSize) {
    let currentLen = len;
    while (currentLen > maxPageSize * 2) {
      currentLen = Math.ceil(currentLen / 2);
    }

    let firstPageSize = Math.ceil(currentLen / 2);
    let secondPageSize = currentLen - firstPageSize;
    pages.splice(index, 0, firstPageSize);
    pages.push(secondPageSize);
    index++;
    len -= currentLen;
  }

  return pages;
};

export default UnitComparisonRadar;
